import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {countries, customerProfessions, UserInfo} from "../types/interfaces";
import {getLoggedInUser} from "../utils/functions";
import {searchSubscriberKyc} from "../api/dataRetrieve";

const AlertCircle = ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
);

const User = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const Phone = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
);

const MapPin = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);

const Users = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
    </svg>
);

const CheckCircle = ({ className = "w-6 h-6" }: { className?: string }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
);

const XCircle = ({ className = "w-6 h-6" }: { className?: string }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
);

const X = ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);




// Form values interface
interface FormValues {
    firstName: string;
    lastName: string;
    msisdn: string;
    alternativePhone: string;
    // Physical Address fields
    plotNumber: string;
    street: string;
    cityTownVillage: string;
    
    // Postal Address fields
    postalCity: string;
    postalAddress: string;
    nextOfKin: string;
    nextOfKinFirstName: string;
    nextOfKinLastName: string;
    nextOfKinPhone: string;
    emailAddress: string;
    accountSegment: string;
    customerProfession: string;
    sex: string;
    idNumber: string;
    documentType: string;
    nationality: string;
}

// Popup interface
interface PopupState {
    show: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
    details?: string;
}

// SMEGA form interface
interface SmegaFormData {
    customerMobileNumber: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    sex: string;
    documentNumber: string;
    documentType: string;
    address: string;
    cityTown: string;
    country: string;
    nextofkincontactnumber: string;
    nextofkinfirstname: string;
    nextofkinlastname: string;
    relationship: string;
    sourceoffunds: string;
    createdby: string;
}

// SMEGA Member Check Response interface
interface SmegaMemberCheckResponse {
    scanId: string;
    matchedNumber: string;
    message: string;
    FOUND: boolean;
    status: string;
    kycType: string;
    customerData?: {
        firstName?: string;
        lastName?: string;
        customerMobileNumber?: string;
        gender?: string;
        dateOfBirth?: string;
        country?: string;
    };
}

// Existing KYC Data interface
interface ExistingKycData {
    exists: boolean;
    customerData?: {
        msisdn: string;
        firstName: string;
        lastName: string;
        fullName: string;
        dateOfBirth: string;
        gender: string;
        idNumber: string;
        idType: string;
        idIssueDate: string;
        idExpiryDate: string;
        address: string;
        city: string;
        country: string;
        email: string;
        postalCode: string;
        language: string;
        userType: string;
        userId: string;
        providerId: string;
        defaultCurrency: string;
        transactionId: string;
    };
    message: string;
}

// Validation function
const validateField = (field: keyof FormValues, value: any, allValues: FormValues): string => {
    switch (field) {
        case 'firstName':
            return !value ? 'First name is required' : '';
        case 'lastName':
            return !value ? 'Last name is required' : '';
        case 'msisdn':
            if (!value) return 'Mobile number is required';
            return '';
        case 'emailAddress':
            if (value) if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email';
            return '';
        case 'idNumber':
            if (!value) return 'ID number is required';
            if (allValues.nationality === 'Botswana' && value.length >= 5) {
                const middleDigit = value.charAt(4);
                if (allValues.sex === 'MALE' && middleDigit !== '1') {
                    return 'For Male, the 5th digit of the ID number must be 1.';
                }
                if (allValues.sex === 'FEMALE' && middleDigit !== '2') {
                    return 'For Female, the 5th digit of the ID number must be 2.';
                }
            }
            return '';
        case 'sex':
            return !value ? 'Sex is required' : '';
        case 'documentType':
            if (!value) return 'Document type is required';
            if (allValues.nationality === 'Botswana' && value !== 'NATIONAL_ID') {
                return 'For Botswana nationality, Document Type must be "National ID".';
            }
            return '';
        case 'postalCity':
            return '';
        case 'postalAddress':
            return '';
        case 'plotNumber':
            return !value ? 'Plot Number is required' : '';
        case 'street':
            return !value ? 'Street is required' : '';
        case 'cityTownVillage':
            return !value ? 'City/Town/Village is required' : '';
        case 'nextOfKinFirstName':
            return !value ? 'Next of kin first name is required' : '';
        case 'nextOfKinLastName':
            return !value ? 'Next of kin last name is required' : '';
        case 'nextOfKinPhone':
            if (!value) return 'Next of kin phone is required';
            return '';
        case 'nationality':
            return !value ? 'Nationality is required' : '';
        case 'accountSegment':
            return !value ? 'Account segment is required' : '';
        case 'customerProfession':
            return !value ? 'Customer profession is required' : '';
        default:
            return '';
    }
};

const accountSegments :string[] = [
    'Individual',
];

export default function RegistrationForm() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const [isSmegaChecking, setIsSmegaChecking] = useState(false);
    const [isSmegaSubmitting, setIsSmegaSubmitting] = useState(false);
    const [isLoadingExistingKyc, setIsLoadingExistingKyc] = useState(false);
    const [isBocraSearching, setIsBocraSearching] = useState(false);
    const [bocraData, setBocraData] = useState<any>(null);
    const [username, setUsername] = useState<string | undefined>();
    const [currentUser, setCurrentUser] = useState<UserInfo|null>(null);


    useEffect(() :void => {
        const getCurrentUser = async () => {
            const loggedInUser: UserInfo | null = await getLoggedInUser()
            if (loggedInUser === null) {
                navigate('/error')
            }
            setCurrentUser(loggedInUser)
            console.log(loggedInUser?.email)
            setUsername(loggedInUser?.email)
        }
        getCurrentUser()

    }, []);


    const [dateValues, setDateValues] = useState({
        dob: new Date().toISOString().split('T')[0],
        documentIssueDate: new Date().toISOString().split('T')[0],
        documentExpiryDate: new Date().toISOString().split('T')[0],
    });
    const [collapsedSections, setCollapsedSections] = useState({
        customerData: true, // Start collapsed
    });

    //base url
    const BASE_URL = process.env.REACT_APP_API_SELFCARE_URL as string;

    const [values, setValues] = useState<FormValues>({
        firstName: '',
        lastName: '',
        msisdn: '',
        alternativePhone: '',
        plotNumber: '',
        street: '',
        cityTownVillage: '',
        nextOfKin: '',
        nextOfKinFirstName: '',
        nextOfKinLastName: '',
        nextOfKinPhone: '',
        emailAddress: '',
        accountSegment: '',
        customerProfession: '',
        sex: '',
        idNumber: '',
        documentType: '',
        postalCity: '',
        postalAddress: '',
        nationality: '',
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [popup, setPopup] = useState<PopupState>({
        show: false,
        type: 'success',
        title: '',
        message: '',
        details: ''
    });

    // SMEGA popup state
    const [smegaPopup, setSmegaPopup] = useState({
        show: false,
        registrationSuccessful: false,
        customerData: {} as FormValues,
        isExistingMember: false,
        memberCheckResult: null as SmegaMemberCheckResponse | null
    });

    // Prevent background (body) scrolling when SMEGA popup is open
    const bodyOverflowRef = React.useRef<string>('');
    useEffect(() => {
        try {
            if (smegaPopup.show) {
                bodyOverflowRef.current = document.body.style.overflow || '';
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = bodyOverflowRef.current || '';
                bodyOverflowRef.current = '';
            }
        } catch (err) {
            // ignore errors (e.g., during SSR or tests)
        }
        return () => {
            try {
                if (bodyOverflowRef.current) {
                    document.body.style.overflow = bodyOverflowRef.current;
                    bodyOverflowRef.current = '';
                }
            } catch (err) {
                // ignore
            }
        };
    }, [smegaPopup.show]);

    // Fixed handleBocraSearch function in RegistrationForm.tsx

    const handleBocraSearch = async () => {
        if (!values.msisdn) {
            setPopup({
                show: true,
                type: 'error',
                title: 'Invalid MSISDN',
                message: 'Please enter a valid 8-10-digit mobile number to search',
                details: ''
            });
            return;
        }

        try {
            setIsBocraSearching(true);

            const data = await searchSubscriberKyc(values.msisdn, undefined);
            setBocraData(data);

            const doc = data.subscriberDocuments?.[0];
            const address = data.addresses?.[0];

            setValues(prev => ({
                ...prev,
                firstName: data.firstName || prev.firstName,
                lastName: data.lastName || prev.lastName,
                msisdn: data.msisdn || prev.msisdn,
                nationality: data.country || prev.nationality,
                sex: data.sex || prev.sex,
                idNumber: doc?.documentNumber || prev.idNumber,
                documentType: doc?.documentType || prev.documentType,
                plotNumber: address?.plotWardBox || prev.plotNumber,
                cityTownVillage: address?.cityTown || prev.cityTownVillage,
                street: prev.street || address?.plotWardBox || '',
            }));

            setDateValues(prev => ({
                ...prev,
                dob: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : prev.dob,
                documentIssueDate: doc?.dateOfIssue ? doc.dateOfIssue.split('T')[0] : prev.documentIssueDate,
                documentExpiryDate: doc?.expiryDate ? doc.expiryDate.split('T')[0] : prev.documentExpiryDate,
            }));

            const fieldsToUpdate = ['firstName', 'lastName', 'msisdn', 'nationality', 'sex', 'idNumber', 'documentType', 'plotNumber', 'cityTownVillage'];
            setErrors(prev => {
                const newErrors = { ...prev };
                fieldsToUpdate.forEach(field => delete newErrors[field]);
                return newErrors;
            });

            const docInfo = doc ? `\nDocument: ${doc.documentNumber} (${doc.documentType})` : '';

            setPopup({
                show: true,
                type: data.verificationState === 'VERIFIED' ? 'success' : 'error',
                title: `Search ${data.verificationState === 'VERIFIED' ? 'Successful' : 'Complete'}`,
                message: `Subscriber found - Verification Status: ${data.verificationState}`,
                details: `Name: ${data.firstName} ${data.lastName}${docInfo}\nAddress: ${address?.plotWardBox || ''}, ${address?.cityTown || ''}${data.verificationReason ? `\nVerification Reason: ${data.verificationReason}` : ''}`,
            });
        } catch (error) {
            setPopup({
                show: true,
                type: 'error',
                title: 'Search Failed',
                message: '',
                details: error instanceof Error ? error.message : 'Unknown error',
            });
        } finally {
            setIsBocraSearching(false);
        }
    };


    // SMEGA form data
    const [smegaFormData, setSmegaFormData] = useState<SmegaFormData>({
        customerMobileNumber: '',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        sex: '',
        documentNumber: '',
        documentType: '',
        address: '',
        cityTown: '',
        country: '',
        nextofkincontactnumber: '',
        nextofkinfirstname: '',
        nextofkinlastname: '',
        relationship: '',
        sourceoffunds: '',
        createdby: ''
    });

    // Existing KYC SMEGA form data
    const [existingSmegaFormData, setExistingSmegaFormData] = useState<SmegaFormData>({
        customerMobileNumber: '',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        sex: '',
        documentNumber: '',
        documentType: '',
        address: '',
        cityTown: '',
        country: '',
        nextofkincontactnumber: '',
        nextofkinfirstname: '',
        nextofkinlastname: '',
        relationship: '',
        sourceoffunds: '',
        createdby: ''
    });

    // Existing KYC data state
    const [existingKycData, setExistingKycData] = useState<ExistingKycData | null>(null);

    const toggleCollapse = (section: keyof typeof collapsedSections) => {
        setCollapsedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Helper function to format response data for display
    const formatResponseData = (data: any): string => {
        if (typeof data === 'string') return data;
        if (typeof data === 'object' && data !== null) {
            return JSON.stringify(data, null, 2);
        }
        return String(data);
    };

    // Fetch existing KYC data
    const fetchExistingKycData = async (msisdn: string) => {
        try {
            setIsLoadingExistingKyc(true);
            console.log('🔍 Fetching existing KYC data for:', msisdn);

            const response = await fetch(`${BASE_URL}/home/smega/existing-kyc/${msisdn}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            const result = await response.json();
            console.log('📥 Existing KYC result:', result);

            if (response.ok && result.success) {
                setExistingKycData(result.data);

                // If KYC exists, populate the existing form
                if (result.data.exists && result.data.customerData) {
                    const kycData = result.data.customerData;

                    setExistingSmegaFormData({
                        customerMobileNumber: kycData.msisdn || msisdn,
                        firstName: kycData.firstName || '',
                        lastName: kycData.lastName || '',
                        dateOfBirth: kycData.dateOfBirth || '',
                        sex: kycData.gender || '',
                        documentNumber: kycData.idNumber || '',
                        documentType: kycData.idType || '',
                        address: kycData.address || '',
                        cityTown: kycData.city || '',
                        country: kycData.country || '',
                        nextofkincontactnumber: '',
                        nextofkinfirstname: '',
                        nextofkinlastname: '',
                        relationship: '',
                        sourceoffunds: '',
                        createdby: username || ''
                    });
                }
                return result.data;
            } else {
                throw new Error(result.error || 'Failed to fetch existing KYC data');
            }
        } catch (error) {
            console.error('❌ Existing KYC fetch error:', error);
            setExistingKycData({
                exists: false,
                message: 'Failed to load existing KYC data'
            });
            throw error;
        } finally {
            setIsLoadingExistingKyc(false);
        }
    };

    // Check SMEGA member status
    const checkSmegaMemberStatus = async (customerData: FormValues) => {
        try {
            setIsSmegaChecking(true);
            console.log('🔍 Checking SMEGA member status...');

            const memberCheckData = {
                FNAME: customerData.firstName,
                LNAME: customerData.lastName,
                MSISDN: customerData.msisdn,
                GENDER: customerData.sex,
                DOB: dateValues.dob,
                COUNTRY: customerData.nationality
            };

            console.log('📤 Member check data:', memberCheckData);

            const response = await fetch(BASE_URL+'/home/smega/membercheck', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(memberCheckData),
            });

            const result = await response.json();
            console.log('📥 Member check result:', result);

            if (response.ok && result.success) {
                console.log(`✅ Member check completed. Found: ${result.data.FOUND}, Status: ${result.data.status}`);

                setSmegaPopup(prev => ({
                    ...prev,
                    memberCheckResult: result.data,
                    isExistingMember: result.data.FOUND
                }));

                // If member exists, populate existing form with available data
                if (result.data.FOUND && result.data.customerData) {
                    const existingData = result.data.customerData;
                    setExistingSmegaFormData(prev => ({
                        ...prev,
                        customerMobileNumber: existingData.customerMobileNumber || customerData.msisdn,
                        firstName: existingData.firstName || customerData.firstName,
                        lastName: existingData.lastName || customerData.lastName,
                        sex: existingData.gender || customerData.sex,
                        dateOfBirth: existingData.dateOfBirth || dateValues.dob,
                        country: existingData.country || customerData.nationality
                    }));
                }

                // Also fetch existing KYC data
                await fetchExistingKycData(customerData.msisdn);

                return result.data;
            } else {
                throw new Error(result.error || 'Member check failed');
            }
        } catch (error) {
            console.error('❌ SMEGA member check error:', error);
            setPopup({
                show: true,
                type: 'error',
                title: 'SMEGA Check Failed',
                message: 'Unable to check SMEGA membership status. Please try again.',
                details: error instanceof Error ? error.message : 'Unknown error'
            });
            throw error;
        } finally {
            setIsSmegaChecking(false);
        }
    };

    // Populate SMEGA form with customer data
    const populateSmegaForm = (customerData: FormValues) => {
        const address = `${customerData.plotNumber} ${customerData.street}`;

        // Use the separate fields if available, otherwise split the combined field
        let nextOfKinFirstName = customerData.nextOfKinFirstName;
        let nextOfKinLastName = customerData.nextOfKinLastName;

        if (!nextOfKinFirstName && !nextOfKinLastName && customerData.nextOfKin) {
            const nextOfKinParts = customerData.nextOfKin.trim().split(' ');
            nextOfKinFirstName = nextOfKinParts[0] || '';
            nextOfKinLastName = nextOfKinParts.slice(1).join(' ') || '';
        }

        setSmegaFormData({
            customerMobileNumber: customerData.msisdn,
            firstName: customerData.firstName,
            lastName: customerData.lastName,
            dateOfBirth: dateValues.dob,
            sex: customerData.sex,
            documentNumber: customerData.idNumber,
            documentType: customerData.documentType,
            address: address,
            cityTown: customerData.cityTownVillage,
            country: customerData.nationality,
            nextofkincontactnumber: customerData.nextOfKinPhone,
            nextofkinfirstname: nextOfKinFirstName,
            nextofkinlastname: nextOfKinLastName,
            relationship: '', // To be filled manually
            sourceoffunds: '', // To be filled manually
            createdby: username || ''
        });
    };

    const handleChange = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const value = e.target.value;
        setValues(prev => ({...prev, [field]: value}));

        // Special handling for nationality change
        if (field === 'nationality') {
            if (value === 'Botswana') {
                setValues(prev => ({...prev, documentType: 'NATIONAL_ID'}));
            } else {
                setValues(prev => ({...prev, documentType: ''}));
            }
        }

        // Special handling for ID number change (auto-select sex for Botswana)
        if (field === 'idNumber' && values.nationality === 'Botswana' && value.length >= 5) {
            const middleDigit = value.charAt(4);
            if (middleDigit === '1') {
                setValues(prev => ({...prev, sex: 'MALE'}));
            } else if (middleDigit === '2') {
                setValues(prev => ({...prev, sex: 'FEMALE'}));
            } else {
                setValues(prev => ({...prev, sex: ''}));
            }
        }

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({...prev, [field]: ''}));
        }
    };

    const handleBlur = (field: keyof FormValues) => () => {
        setTouched(prev => ({...prev, [field]: true}));
        const error = validateField(field, values[field], values);
        setErrors(prev => ({...prev, [field]: error}));
    };

    const clearForm = () => {
        setValues({
            firstName: '',
            lastName: '',
            msisdn: '',
            alternativePhone: '',
            plotNumber: '',
            street: '',
            cityTownVillage: '',
            nextOfKin: '',
            nextOfKinFirstName: '',
            nextOfKinLastName: '',
            nextOfKinPhone: '',
            emailAddress: '',
            accountSegment: '',
            customerProfession: '',
            sex: '',
            idNumber: '',
            documentType: '',
            postalCity: '',
            postalAddress: '',
            nationality: '',
        });
        setDateValues({
            dob: new Date().toISOString().split('T')[0],
            documentIssueDate: new Date().toISOString().split('T')[0],
            documentExpiryDate: new Date().toISOString().split('T')[0],
        });
        setErrors({});
        setTouched({});
        setSmegaPopup(prev => ({...prev, registrationSuccessful: false}));
        setExistingKycData(null);
    };

    const closePopup = () => {
        setPopup(prev => ({...prev, show: false}));
    };

    const formatDate = (date: string) => date;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!username) {
            setPopup({
                show: true,
                type: 'error',
                title: 'Authentication Error',
                message: 'User session not found. Please login again.',
                details: ''
            });
            return;
        }

        // Validate all fields
        const newErrors: Record<string, string> = {};
        (Object.keys(values) as Array<keyof FormValues>).forEach(field => {
            const error = validateField(field, values[field], values);
            if (error) newErrors[field] = error;
        });

        setErrors(newErrors);
        setTouched((Object.keys(values) as Array<keyof FormValues>).reduce((acc, key) => ({...acc, [key]: true}), {}));

        // Show validation error popup
        if (Object.keys(newErrors).some(key => newErrors[key])) {
            setPopup({
                show: true,
                type: 'error',
                title: 'Validation Error',
                message: 'Please correct the errors in the form before submitting.',
                details: ''
            });
            return;
        }

        try {
            setIsSubmitting(true);
            console.log('Attempting to submit form...');
            console.log('Form values:', values);
            console.log('Using username/userid:', username);

            // Combine next of kin names for submission
            const combinedNextOfKin = `${values.nextOfKinFirstName} ${values.nextOfKinLastName}`.trim();

            const formatted = {
                firstName: values.firstName,
                lastName: values.lastName,
                msisdn: values.msisdn,
                alternativePhone: values.alternativePhone || '',
                plotNumber: values.plotNumber,
                street: values.street,
                cityTownVillage: values.cityTownVillage,
                city: values.postalCity,
                billAddress4: values.postalAddress,
                zipCode: '',
                nextOfKin: combinedNextOfKin, // Use combined name
                nextOfKinPhone: values.nextOfKinPhone,
                emailAddress: values.emailAddress,
                gender: values.sex,
                dateOfBirth: formatDate(dateValues.dob),
                nationality: values.nationality,
                idType: values.documentType,
                idNumber: ['NATIONAL_ID','PASSPORT','REFUGEE_ID'].includes(values.documentType) ? values.idNumber : '',
                idIssueDate: ['NATIONAL_ID','PASSPORT','REFUGEE_ID'].includes(values.documentType) ? formatDate(dateValues.documentIssueDate) : '',
                idExpiryDate: ['NATIONAL_ID','PASSPORT','REFUGEE_ID'].includes(values.documentType) ? formatDate(dateValues.documentExpiryDate) : '',
                newPhoneNumber: '',
                // Populate only selected doc type, others empty
                residencePermitNo: values.documentType === 'RESIDENCE_PERMIT' ? values.idNumber : '',
                residencePermitDateOfIssue: values.documentType === 'RESIDENCE_PERMIT' ? formatDate(dateValues.documentIssueDate) : '',
                residencePermitDateOfExpiry: values.documentType === 'RESIDENCE_PERMIT' ? formatDate(dateValues.documentExpiryDate) : '',
                workPermitNo: values.documentType === 'WORK_PERMIT' ? values.idNumber : '',
                workPermitDateOfIssue: values.documentType === 'WORK_PERMIT' ? formatDate(dateValues.documentIssueDate) : '',
                workPermitDateOfExpiry: values.documentType === 'WORK_PERMIT' ? formatDate(dateValues.documentExpiryDate) : '',
                visaNo: values.documentType === 'VISA' ? values.idNumber : '',
                visaDateOfIssue: values.documentType === 'VISA' ? formatDate(dateValues.documentIssueDate) : '',
                visaDateOfExpiry: values.documentType === 'VISA' ? formatDate(dateValues.documentExpiryDate) : '',
                refugeeIdNo: '',
                customerProfession: values.customerProfession,
            };

            console.log('Payload:', JSON.stringify(formatted, null, 2));

            //  fetch call with userid header
            const response = await fetch(BASE_URL+'/home/register-subscriber', {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                    'userid': username,
                },
                body: JSON.stringify(formatted),
            });

            const data = await response.json();
            console.log('API Response:', data);

            // Check for the specific "number belongs to another subscriber" error
            if (data.action === 'EXCEPTION' && data.message && data.message.includes('belongs to another Subscriber')) {
                setPopup({
                    show: true,
                    type: 'error',
                    title: 'Number Already Registered',
                    message: data.message,
                });
                return;
            }

            // Handle other response cases (success or other errors)
            const {
                billingMessage,
                currentVerificationStatus,
                message: errorMessageFromServer,
                details: serverDetail,
                success,
            } = data;

            const verificationState = currentVerificationStatus?.state ?? 'N/A';
            const verificationReason = currentVerificationStatus?.verificationReason ?? 'N/A';
            const billingMsg = billingMessage?.message ?? 'N/A';
            const billingDetails = billingMessage?.details ?? 'N/A';

            // Check for various failure conditions
            const hasExternalIdError = billingDetails.toLowerCase().includes('can not find the external id');
            const hasFailedVerification = verificationState === 'FAILED_VERIFICATION';
            const hasPendingVerification = verificationState === 'PENDING_VERIFICATION';
            const hasVerificationFailure = verificationState.includes('FAILED') || verificationState === 'REJECTED' || verificationState.includes('PENDING');

            let popupMessage = '';
            let popupDetails = '';
            let popupType: 'success' | 'error'
            let popupTitle = '';

            // Check for failure conditions (BOCRA STATUS)
            if (hasExternalIdError) {
                popupType = 'error';
                popupTitle = 'External ID Validation Error';
                popupMessage = 'Number is not a BTC number';
                popupDetails = billingDetails;
            } else if (hasFailedVerification || hasPendingVerification || hasVerificationFailure) {
                popupType = 'error';
                popupTitle = hasPendingVerification ? 'Registration Pending' : 'Registration Failed';
                popupMessage = hasPendingVerification
                    ? 'Registration submitted but verification is still pending. Please wait for verification to complete.'
                    : 'Registration could not be completed due to verification failure.';
                popupDetails = `VERIFICATION STATE: ${verificationState}\n` +
                    `REASON: ${verificationReason}\n` +
                    `BILLING MESSAGE: ${billingMsg}\n` +
                    `DETAILS: ${billingDetails}`;
            } else if (!response.ok) {
                // HTTP error responses
                popupType = 'error';
                popupTitle = `Registration Failed (${response.status})`;
                popupMessage = errorMessageFromServer ?? 'There was an issue with your registration.';
                popupDetails = serverDetail
                    ?? `VERIFICATION STATE: ${verificationState}\n` +
                    `REASON: ${verificationReason}\n` +
                    `BILLING MESSAGE: ${billingMsg}\n` +
                    `DETAILS: ${billingDetails}`;
                console.log(' API Error Response:', data);
            } else {
                // Only show success if HTTP is OK AND no verification failures
                popupType = 'success';
                popupTitle = 'Registration Complete';
                popupMessage = 'Your registration has been successfully submitted!';
                popupDetails = `VERIFICATION STATE: ${verificationState}\n` +
                    `REASON: ${verificationReason}\n` +
                    `BILLING MESSAGE: ${billingMsg}\n` +
                    `DETAILS: ${billingDetails}`;
            }

            setPopup({
                show: true,
                type: popupType,
                title: popupTitle,
                message: popupMessage,
                details: popupDetails,
            });

            // Enable SMEGA registration ONLY after truly successful registration
            if (popupType === 'success') {
                setSmegaPopup(prev => ({
                    ...prev,
                    registrationSuccessful: true,
                    customerData: values
                }));
                populateSmegaForm(values);
            }

        } catch (error) {
            // Network or other unexpected errors
            console.error(' Unexpected error during registration:', error);
            setPopup({
                show: true,
                type: 'error',
                title: 'Connection Error',
                message: 'Unable to connect to the registration service. Please check your connection and try again.',
                details: error instanceof Error ? error.message : 'Unknown error occurred'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRegisterSmega = async () => {
        // Populate the SMEGA form from current values and attempt to fetch any
        // existing SMEGA KYC for the provided MSISDN so the popup can show the
        // existing data on open. This replaces the previous member-check flow.
            populateSmegaForm(values);

            try {
            // Try to fetch existing KYC data; `fetchExistingKycData` will populate
            // `existingSmegaFormData` when data exists.
            const existing = await fetchExistingKycData(values.msisdn);

                setSmegaPopup(prev => ({
                    ...prev,
                isExistingMember: !!(existing && existing.exists),
                memberCheckResult: existing || null,
                    show: true
                }));
        } catch (err) {
            // If the fetch fails, still open the popup but mark as non-existing.
            console.error('Failed to fetch existing SMEGA KYC:', err);
            setSmegaPopup(prev => ({ ...prev, isExistingMember: false, memberCheckResult: null, show: true }));
        }
    };

    const handleSmegaFormChange = (field: keyof SmegaFormData, value: string, isExisting = false) => {
        if (isExisting) {
            setExistingSmegaFormData(prev => ({
                ...prev,
                [field]: value
            }));
        } else {
            setSmegaFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleSmegaSubmit = async (isExisting = false) => {
        const dataToSubmit = isExisting ? existingSmegaFormData : smegaFormData;

        // Validate required fields
        const requiredFields = [
            'customerMobileNumber', 'firstName', 'lastName', 'dateOfBirth', 'sex',
            'documentNumber', 'documentType', 'address', 'cityTown', 'country',
            'nextofkincontactnumber', 'nextofkinfirstname', 'nextofkinlastname',
            'relationship', 'sourceoffunds', 'createdby'
        ];

        const missingFields = requiredFields.filter(field =>
            !dataToSubmit[field as keyof SmegaFormData] ||
            dataToSubmit[field as keyof SmegaFormData].trim() === ''
        );

        if (missingFields.length > 0) {
            setPopup({
                show: true,
                type: 'error',
                title: 'SMEGA Validation Error',
                message: 'Please fill in all required fields before submitting.',
                details: `Missing fields: ${missingFields.join(', ')}`
            });
            return;
        }

        try {
            setIsSmegaSubmitting(true);
            console.log(' Submitting SMEGA registration:', dataToSubmit);

            const response = await fetch(BASE_URL+'/home/smega/registration', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSubmit),
            });

            console.log(' Response status:', response.status);
            const result = await response.json();
            console.log(' Response data:', result);

            // Close SMEGA popup first
            setSmegaPopup(prev => ({ ...prev, show: false }));

            if (response.ok && result.success) {
                setPopup({
                    show: true,
                    type: 'success',
                    title: 'SMEGA Registration Successful',
                    message: `${isExisting ? 'Existing KYC' : 'New'} SMEGA registration completed successfully!`,
                });
            } else {
                // Handle different error scenarios
                let errorTitle = 'SMEGA Registration Failed';
                let errorMessage = 'Failed to register with SMEGA';
                let errorDetails = 'Please try again or contact support';

                // Check if this is a duplicate ID error - specifically check for the exact message
                if (result.MESSAGE && result.MESSAGE === 'Subscriber with same Identification No. already exists') {
                    errorTitle = 'Duplicate Registration';
                    errorMessage = 'A subscriber with the same identification number is already registered in SMEGA.';
                    // errorDetails = `Transaction ID: ${result.TXNID || 'N/A'}\nTransaction Status: ${result.TXNSTATUS || 'N/A'}\nTrace ID: ${result.TRID || 'N/A'}\nType: ${result.TYPE || 'N/A'}`;
                } else if (result.MESSAGE && result.MESSAGE.includes('already exists')) {
                    errorTitle = 'Duplicate Registration';
                    errorMessage = 'This subscriber is already registered in SMEGA.';
                    errorDetails = `Transaction ID: ${result.TXNID || 'N/A'}\nTransaction Status: ${result.TXNSTATUS || 'N/A'}\nTrace ID: ${result.TRID || 'N/A'}\nType: ${result.TYPE || 'N/A'}`;
                } else if (result.error) {
                    errorMessage = result.error;
                    errorDetails = formatResponseData(result.detail || result);
                } else if (result.MESSAGE) {
                    // Handle other API error messages
                    errorMessage = result.MESSAGE;
                    errorDetails = `Transaction ID: ${result.TXNID || 'N/A'}\nTransaction Status: ${result.TXNSTATUS || 'N/A'}\nTrace ID: ${result.TRID || 'N/A'}\nType: ${result.TYPE || 'N/A'}`;
                } else {
                    // Fallback for unknown error format
                    errorMessage = 'An unexpected error occurred during SMEGA registration';
                    errorDetails = formatResponseData(result);
                }

                setPopup({
                    show: true,
                    type: 'error',
                    title: errorTitle,
                    message: errorMessage,
                });
            }
        } catch (error) {
            console.error(' SMEGA registration error:', error);
            setSmegaPopup(prev => ({ ...prev, show: false }));

            setPopup({
                show: true,
                type: 'error',
                title: 'SMEGA Connection Error',
                message: 'Unable to connect to SMEGA service. Please try again.',
                details: error instanceof Error ? error.message : 'Unknown error occurred'
            });
        } finally {
            setIsSmegaSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            {/* Popup Modal */}
            {popup.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
                        <button
                            onClick={closePopup}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-5 h-5"/>
                        </button>

                        <div className="text-center">
                            {popup.type === 'success' ? (
                                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4"/>
                            ) : (
                                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4"/>
                            )}

                            <h3 className={`text-xl font-bold mb-2 ${popup.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                                {popup.title}
                            </h3>

                            <p className="text-gray-600 mb-4">{popup.message}</p>

                            {popup.details && (
                                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-left">
                                    <pre className="text-sm text-gray-700 whitespace-pre-wrap">{popup.details}</pre>
                                </div>
                            )}

                            <button
                                onClick={closePopup}
                                className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors ${
                                    popup.type === 'success'
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                }`}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* SMEGA Registration Popup */}
            {smegaPopup.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-y-auto p-6 relative">
                        <button
                            onClick={() => setSmegaPopup({...smegaPopup, show: false})}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
                        >
                            <X className="w-5 h-5"/>
                        </button>

                        <div className="text-center mb-6">
                            <img src={process.env.PUBLIC_URL + '/smega.png'} alt="SMEGA" className="mx-auto h-12 mb-2" />
                            <h2 className="text-2xl font-bold text-green-700 mb-2">SMEGA Registration</h2>
                            {/*{smegaPopup.memberCheckResult && (*/}
                            {/*    <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded-lg">*/}
                            {/*        Member Status: {smegaPopup.memberCheckResult.FOUND ? 'Found' : 'Not Found'} |*/}
                            {/*        Status: {smegaPopup.memberCheckResult.status} |*/}
                            {/*        KYC: {smegaPopup.memberCheckResult.kycType}*/}
                            {/*    </div>*/}
                            {/*)}*/}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Register SMEGA Form - Left Side */}
                            <div className="border border-green-200 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-green-700 mb-4 text-center">New SMEGA Registration</h3>

                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {/* Collapsible Customer Data Section */}
                                    <div className="border border-gray-200 rounded-lg">
                                        <button
                                            type="button"
                                            onClick={() => toggleCollapse('customerData')}
                                            className="w-full px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-t-lg flex justify-between items-center text-sm font-medium text-gray-700 transition-colors"
                                        >
                                            <span>View Details...</span>
                                            <svg
                                                className={`w-4 h-4 transition-transform ${collapsedSections.customerData ? 'rotate-0' : 'rotate-180'}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {!collapsedSections.customerData && (
                                            <div className="p-3 space-y-3 bg-green-50 rounded-b-lg">
                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">Customer Mobile Number</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.customerMobileNumber}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">First Name</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.firstName}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">Last Name</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.lastName}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">Date of Birth</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.dateOfBirth}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">Sex</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.sex}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">Document Number</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.documentNumber}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">Document Type</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.documentType}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">Address</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.address}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">City/Town</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.cityTown}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">Country</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.country}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-green-700 mb-1">Created By</label>
                                                    <input
                                                        type="text"
                                                        value={smegaFormData.createdby}
                                                        disabled
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-blue-700 text-sm"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Next of Kin Fields - MOVED OUTSIDE collapsed section */}
                                    <div className="border border-gray-300 rounded-lg p-3 bg-green-50">
                                        <h4 className="text-sm font-semibold text-green-700 mb-3">Next of Kin Information</h4>

                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-green-700 mb-1">Next of Kin Contact Number</label>
                                                <input
                                                    type="text"
                                                    value={smegaFormData.nextofkincontactnumber}
                                                    disabled
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-green-700 mb-1">Next of Kin First Name</label>
                                                <input
                                                    type="text"
                                                    value={smegaFormData.nextofkinfirstname}
                                                    disabled
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-green-700 mb-1">Next of Kin Last Name</label>
                                                <input
                                                    type="text"
                                                    value={smegaFormData.nextofkinlastname}
                                                    disabled
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-green-50 text-gray-700 text-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Manual Input Fields - Always visible */}
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-green-700 mb-1">Relationship *</label>
                                            <select
                                                value={smegaFormData.relationship}
                                                onChange={(e) => handleSmegaFormChange('relationship', e.target.value)}
                                                className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                                            >
                                                <option value="">Select Relationship</option>
                                                <option value="Mother">Mother</option>
                                                <option value="Father">Father</option>
                                                <option value="Spouse">Spouse</option>
                                                <option value="Brother">Brother</option>
                                                <option value="Sister">Sister</option>
                                                <option value="Son">Son</option>
                                                <option value="Daughter">Daughter</option>
                                                <option value="Friend">Friend</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-green-700 mb-1">Source of Funds *</label>
                                            <select
                                                value={smegaFormData.sourceoffunds}
                                                onChange={(e) => handleSmegaFormChange('sourceoffunds', e.target.value)}
                                                className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                                            >
                                                <option value="">Select Source of Funds</option>
                                                <option value="Income">Income</option>
                                                <option value="Business">Business</option>
                                                <option value="Savings">Savings</option>
                                                <option value="Investment">Investment</option>
                                                <option value="Allowance">Allowance</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleSmegaSubmit(false)}
                                    disabled={isSmegaSubmitting}
                                    className="w-full mt-4 bg-gradient-to-r from-green-700 to-green-700 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-700 hover:to-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSmegaSubmitting ? 'Submitting...' : 'Submit SMEGA Registration'}
                                </button>
                            </div>

                            {/* Existing KYC Form - Right Side (unchanged) */}
                            <div className="border border-blue-200 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-blue-700 mb-4 text-center">
                                    Existing SMEGA
                                    {isLoadingExistingKyc && (
                                        <span className="ml-2 text-sm text-gray-500">(Loading...)</span>
                                    )}
                                </h3>

                                {existingKycData?.exists ? (
                                    <div className="space-y-3 max-h-96 overflow-y-auto">
                                        {/* Display existing KYC data - all fields are non-editable */}
                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Customer Mobile Number</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.msisdn || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">First Name</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.firstName || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Last Name</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.lastName || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Full Name</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.fullName || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Date of Birth</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.dateOfBirth || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Gender</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.gender || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">ID Number</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.idNumber || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">ID Type</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.idType || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">ID Issue Date</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.idIssueDate || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">ID Expiry Date</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.idExpiryDate || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Address</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.address || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">City</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.city || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Country</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.country || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Email</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.email || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Postal Code</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.postalCode || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">User Type</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.userType || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">User ID</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.userId || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Provider ID</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.providerId || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Default Currency</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.defaultCurrency || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-blue-700 mb-1">Transaction ID</label>
                                            <input
                                                type="text"
                                                value={existingKycData.customerData?.transactionId || ''}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-blue-50 text-gray-700 text-sm"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <div className="text-gray-500 mb-4">
                                            {isLoadingExistingKyc ? (
                                                <div className="flex items-center justify-center">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                                    <span className="ml-3">Loading existing KYC data...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <XCircle className="w-16 h-16 text-gray-400 mx-auto mb-2"/>
                                                    <p className="text-lg font-medium">KYC Not Available</p>
                                                    <p className="text-sm">{existingKycData?.message || 'No existing KYC data found for this customer'}</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="text-center mb-8">
                        {/* Back button */}
                        <div className="flex items-center justify-between mb-4">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="flex items-center text-gray-600 hover:text-green-700 transition-colors duration-200 group"
                            >
                                <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back
                            </button>
                            <div></div>
                        </div>

                        <User className="w-12 h-12 text-green-700 mx-auto mb-4"/>
                        <h1 className="text-3xl font-bold text-green-700 mb-2">Customer Registration</h1>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Personal Information Section */}
                        <div className="border-b pb-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                <User className="w-5 h-5"/>
                                <span className="ml-2">Personal Information</span>
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="relative">
                                    <label className="block text-sm font-medium text-green-700 mb-2">Mobile Number *</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="tel"
                                            maxLength={10}
                                            value={values.msisdn}
                                            onChange={handleChange('msisdn')}
                                            onBlur={handleBlur('msisdn')}
                                            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleBocraSearch}
                                            disabled={isBocraSearching}
                                            className="bg-green-700 text-white px-4 py-3 rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isBocraSearching ? (
                                                <div className="flex items-center">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                    Searching...
                                                </div>
                                            ) : (
                                                'Search'
                                            )}
                                        </button>
                                    </div>
                                    {touched.msisdn && errors.msisdn && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.msisdn}</span>
                                        </p>
                                    )}
                                    {bocraData && (
                                        <div className="mt-2 text-sm text-blue-700 bg-blue-50 p-2 rounded-lg">
                                            <div className="font-medium">BOCRA Verification: {bocraData.verificationState}</div>
                                            {bocraData.verificationReason && (
                                                <div className="mt-1">Reason: {bocraData.verificationReason}</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                
                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">First Name
                                        *</label>
                                    <input
                                        type="text"
                                        value={values.firstName}
                                        onChange={handleChange('firstName')}
                                        onBlur={handleBlur('firstName')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.firstName && errors.firstName && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.firstName}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Last Name *</label>
                                    <input
                                        type="text"
                                        value={values.lastName}
                                        onChange={handleChange('lastName')}
                                        onBlur={handleBlur('lastName')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.lastName && errors.lastName && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.lastName}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Date of Birth
                                        *</label>
                                    <input
                                        type="date"
                                        value={dateValues.dob}
                                        onChange={(e) => setDateValues(prev => ({...prev, dob: e.target.value}))}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Nationality
                                        *</label>
                                    <select
                                        value={values.nationality}
                                        onChange={handleChange('nationality')}
                                        onBlur={handleBlur('nationality')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    >
                                        <option value="">Select Nationality</option>
                                        {countries.map(country => (
                                            <option key={country} value={country}>{country}</option>
                                        ))}
                                    </select>
                                    {touched.nationality && errors.nationality && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.nationality}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">ID Number *</label>
                                    <input
                                        type="text"
                                        value={values.idNumber}
                                        onChange={handleChange('idNumber')}
                                        onBlur={handleBlur('idNumber')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.idNumber && errors.idNumber && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.idNumber}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Sex *</label>
                                    <select
                                        value={values.sex}
                                        onChange={handleChange('sex')}
                                        onBlur={handleBlur('sex')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    >
                                        <option value="">Select Sex</option>
                                        <option value="MALE">MALE</option>
                                        <option value="FEMALE">FEMALE</option>
                                    </select>
                                    {touched.sex && errors.sex && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.sex}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Document Type
                                        *</label>
                                    <select
                                        value={values.documentType}
                                        onChange={handleChange('documentType')}
                                        onBlur={handleBlur('documentType')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    >
                                        <option value="">Select Document Type</option>
                                        <option value="NATIONAL_ID">National ID</option>
                                        <option value="PASSPORT">Passport</option>
                                        <option value="RESIDENCE_PERMIT">Residence Permit</option>
                                        <option value="WORK_PERMIT">Work Permit</option>
                                        <option value="VISA">Visa</option>
                                        <option value="REFUGEE_ID">Refugee Id</option>
                                        
                                    </select>
                                    {touched.documentType && errors.documentType && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.documentType}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Document Issue Date
                                        *</label>
                                    <input
                                        type="date"
                                        value={dateValues.documentIssueDate}
                                        onChange={(e) => setDateValues(prev => ({
                                            ...prev,
                                            documentIssueDate: e.target.value
                                        }))}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Document Expiry
                                        Date *</label>
                                    <input
                                        type="date"
                                        value={dateValues.documentExpiryDate}
                                        onChange={(e) => setDateValues(prev => ({
                                            ...prev,
                                            documentExpiryDate: e.target.value
                                        }))}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Account Details Section */}
                        <div className="border-b pb-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                <User className="w-5 h-5"/>
                                <span className="ml-2">Account Details</span>
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Account Segment *</label>
                                    <select
                                        value={values.accountSegment}
                                        onChange={handleChange('accountSegment')}
                                        onBlur={handleBlur('accountSegment')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    >
                                        <option value="">Select Account Segment</option>
                                        {accountSegments.map(seg => (
                                            <option key={seg} value={seg}>{seg}</option>
                                        ))}
                                    </select>
                                    {touched.accountSegment && errors.accountSegment && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.accountSegment}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Customer Profession *</label>
                                    <select
                                        value={values.customerProfession}
                                        onChange={handleChange('customerProfession')}
                                        onBlur={handleBlur('customerProfession')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    >
                                        <option value="">Select Customer Profession</option>
                                        {customerProfessions.map(prof => (
                                            <option key={prof} value={prof}>{prof}</option>
                                        ))}
                                    </select>
                                    {touched.customerProfession && errors.customerProfession && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.customerProfession}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Contact Information Section */}
                        <div className="border-b pb-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                <Phone className="w-5 h-5"/>
                                <span className="ml-2">Contact Information</span>
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Alternative Phone *</label>
                                    <input
                                        type="tel"
                                        maxLength={20}
                                        value={values.alternativePhone}
                                        onChange={handleChange('alternativePhone')}
                                        onBlur={handleBlur('alternativePhone')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.alternativePhone && errors.alternativePhone && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.alternativePhone}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Email Address
                                        </label>
                                    <input
                                        type="email"
                                        value={values.emailAddress}
                                        onChange={handleChange('emailAddress')}
                                        onBlur={handleBlur('emailAddress')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="example@email.com"
                                    />
                                    {touched.emailAddress && errors.emailAddress && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.emailAddress}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Physical Address Section */}
                        <div className="border-b pb-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                <MapPin className="w-5 h-5"/>
                                <span className="ml-2">Physical Address</span>
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Plot Number
                                        *</label>
                                    <input
                                        type="text"
                                        value={values.plotNumber}
                                        onChange={handleChange('plotNumber')}
                                        onBlur={handleBlur('plotNumber')}
                                        maxLength={10}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.plotNumber && errors.plotNumber && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.plotNumber}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Street *</label>
                                    <input
                                        type="text"
                                        value={values.street}
                                        onChange={handleChange('street')}
                                        onBlur={handleBlur('street')}
                                        maxLength={20}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.street && errors.street && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.street}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">City/Town/Village
                                        *</label>
                                    <input
                                        type="text"
                                        value={values.cityTownVillage}
                                        onChange={handleChange('cityTownVillage')}
                                        onBlur={handleBlur('cityTownVillage')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.cityTownVillage && errors.cityTownVillage && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.cityTownVillage}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Postal Address Section */}
                        <div className="border-b pb-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                <MapPin className="w-5 h-5"/>
                                <span className="ml-2">Postal Address</span>
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Postal City
                                        </label>
                                    <input
                                        type="text"
                                        value={values.postalCity}
                                        onChange={handleChange('postalCity')}
                                        onBlur={handleBlur('postalCity')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.postalCity && errors.postalCity && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.postalCity}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Postal Address
                                        </label>
                                    <input
                                        type="text"
                                        value={values.postalAddress}
                                        onChange={handleChange('postalAddress')}
                                        onBlur={handleBlur('postalAddress')}
                                        maxLength={18}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.postalAddress && errors.postalAddress && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.postalAddress}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Next of Kin Section */}
                        <div className="border-b pb-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                <Users className="w-5 h-5"/>
                                <span className="ml-2">Next of Kin</span>
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">First Name *</label>
                                    <input
                                        type="text"
                                        value={values.nextOfKinFirstName}
                                        onChange={handleChange('nextOfKinFirstName')}
                                        onBlur={handleBlur('nextOfKinFirstName')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.nextOfKinFirstName && errors.nextOfKinFirstName && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.nextOfKinFirstName}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Last Name *</label>
                                    <input
                                        type="text"
                                        value={values.nextOfKinLastName}
                                        onChange={handleChange('nextOfKinLastName')}
                                        onBlur={handleBlur('nextOfKinLastName')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.nextOfKinLastName && errors.nextOfKinLastName && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.nextOfKinLastName}</span>
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-2">Phone *</label>
                                    <input
                                        type="tel"
                                        maxLength={20}
                                        value={values.nextOfKinPhone}
                                        onChange={handleChange('nextOfKinPhone')}
                                        onBlur={handleBlur('nextOfKinPhone')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                    {touched.nextOfKinPhone && errors.nextOfKinPhone && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4"/>
                                            <span className="ml-1">{errors.nextOfKinPhone}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Submit and Clear Buttons */}
                        <div className="pt-6">
                            {isSubmitting ? (
                                <div className="flex items-center justify-center py-4">
                                    <div
                                        className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                                    <span className="ml-3 text-green-600 font-medium">Submitting registration...</span>
                                </div>
                            ) : (
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-gradient-to-r from-green-700 to-green-700 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-green-600 hover:to-green-600 transform hover:scale-105 transition-all duration-200 shadow-lg"
                                    >
                                        Submit Registration
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleRegisterSmega}
                                        disabled={!smegaPopup.registrationSuccessful || isSmegaChecking}
                                        className={`flex-1 py-4 px-6 rounded-lg font-semibold text-lg transform hover:scale-105 transition-all duration-200 shadow-lg ${
                                            smegaPopup.registrationSuccessful && !isSmegaChecking
                                                ? 'bg-white text-green-700 border border-green-600 hover:bg-green-50'
                                                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                        }`}
                                    >
                                    {isSmegaChecking ? 'Checking SMEGA...' : (
                                        <>
                                            <img src={process.env.PUBLIC_URL + '/smega.png'} alt="SMEGA" className="inline-block h-5 w-auto mr-2 align-middle" />
                                            Register Smega
                                        </>
                                    )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={clearForm}
                                        className="flex-1 sm:flex-none sm:px-8 bg-gray-500 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-gray-600 transform hover:scale-105 transition-all duration-200 shadow-lg"
                                    >
                                        Clear Form
                                    </button>
                                </div>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}